import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMcpSessionStore } from "@/stores/mcpSessionStore";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertCircle, ExternalLink, RefreshCw, Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { PromptsSection } from "./CompactInspector/PromptsSection";
import { ResourcesSection } from "./CompactInspector/ResourcesSection";
import { ToolsSection } from "./CompactInspector/ToolsSection";
import { useCompactInspectorData } from "./CompactInspector/useCompactInspectorData";

interface CompactInspectorProps {
  projectId: string;
}

// Content component for nested use (without Card wrapper)
export function CompactInspectorContent({ projectId }: CompactInspectorProps) {
  const { tools, resources, prompts, isDeployed, isLoading, session } =
    useCompactInspectorData(projectId);

  // Calculate expansion state - expand sections with items, collapse empty sections
  const expandedSections = useMemo(() => {
    return {
      tools: tools.length > 0,
      resources: resources.length > 0,
      prompts: prompts.length > 0,
    };
  }, [tools.length, resources.length, prompts.length]);

  // State for manual toggles (overrides automatic expansion)
  const [manualToggles, setManualToggles] = useState<Record<string, boolean>>(
    {},
  );

  const toggleSection = (section: keyof typeof expandedSections) => {
    setManualToggles((prev) => ({
      ...prev,
      [section]:
        prev[section] !== undefined
          ? !prev[section]
          : !expandedSections[section],
    }));
  };

  // Final expansion state - manual toggles override automatic behavior
  const finalExpandedSections = useMemo(() => {
    return {
      tools:
        manualToggles.tools !== undefined
          ? manualToggles.tools
          : expandedSections.tools,
      resources:
        manualToggles.resources !== undefined
          ? manualToggles.resources
          : expandedSections.resources,
      prompts:
        manualToggles.prompts !== undefined
          ? manualToggles.prompts
          : expandedSections.prompts,
    };
  }, [expandedSections, manualToggles]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center h-24 text-muted-foreground">
          <div className="text-center">
            <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
            <p className="text-sm">Loading MCP inspector...</p>
          </div>
        </div>
      </div>
    );
  }

  if (session.status === "error") {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center h-24 text-destructive">
          <div className="text-center">
            <AlertCircle className="w-6 h-6 mx-auto mb-2" />
            <p className="text-sm font-medium">Failed to connect to MCP</p>
            {session.error && (
              <p className="text-xs text-muted-foreground mt-1">
                {session.error}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ToolsSection
        tools={tools}
        projectId={projectId}
        isDeployed={isDeployed}
        isExpanded={finalExpandedSections.tools}
        onToggle={() => toggleSection("tools")}
      />

      <ResourcesSection
        resources={resources}
        projectId={projectId}
        isDeployed={isDeployed}
        isExpanded={finalExpandedSections.resources}
        onToggle={() => toggleSection("resources")}
      />

      <PromptsSection
        prompts={prompts}
        projectId={projectId}
        isDeployed={isDeployed}
        isExpanded={finalExpandedSections.prompts}
        onToggle={() => toggleSection("prompts")}
      />

      {!isDeployed && (
        <div className="flex items-center justify-center p-4 border rounded-lg bg-muted/10 border-muted">
          <div className="text-center space-y-2">
            <AlertCircle className="w-6 h-6 mx-auto text-muted-foreground" />
            <div className="text-sm font-medium text-muted-foreground">
              Deploy your app to enable MCP Inspector
            </div>
            <div className="text-xs text-muted-foreground max-w-64">
              Once deployed, the MCP Inspector will automatically discover and
              display available tools, resources, and prompts from your MCP
              server.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CompactInspector({ projectId }: CompactInspectorProps) {
  const queryClient = useQueryClient();
  const clearSession = useMcpSessionStore((state) => state.clearSession);
  const clearInitialization = useMcpSessionStore(
    (state) => state.clearInitialization,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Step 1: Cancel any ongoing queries to prevent race conditions
      await queryClient.cancelQueries({
        queryKey: ["MCP", projectId],
      });

      // Step 2: Clear cached query data first (but don't trigger refetch yet)
      queryClient.removeQueries({
        queryKey: ["MCP", projectId],
      });

      // Step 3: Clear MCP session and initialization state
      clearSession(projectId);
      clearInitialization(projectId);

      // Step 4: Wait a moment for state to settle before allowing refetch
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Step 5: Now invalidate to trigger fresh fetches with clean state
      await queryClient.invalidateQueries({
        queryKey: ["MCP", projectId],
      });
    } catch (error) {
      console.error("Failed to refresh MCP data:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [projectId, queryClient, clearSession, clearInitialization]);

  return (
    <Card className="h-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="w-5 h-5 text-primary" />
            MCP Inspector
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Refresh MCP data"
            >
              <RefreshCw
                className={`w-3 h-3 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link
                to="/projects/$projectId"
                params={{ projectId }}
                search={{ tab: "inspector" }}
              >
                <ExternalLink className="w-3 h-3" />
              </Link>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <CompactInspectorContent projectId={projectId} />
      </CardContent>
    </Card>
  );
}
